import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ISelectOption, LifecycleAware } from '@lsq-frontend/common-ui';
import {
    ACCOUNT_NUMBER_MAX_LENGTH,
    ROUTING_NUMBER_LENGTH
} from '../../../../../constants/bank.constants';
import {
    AdminManageBuyerConfigurationService,
    FboAccountType
} from './buyer-configuration.service';

@LifecycleAware()
@Component({
    selector: 'admin-buyer-configuration-tpm-configuration',
    template: `
        <buyer-configuration-section
            [hasClickedSave]="hasClickedSave"
            sectionFormControlName="tpmEnabled"
            [sectionFormGroup]="tpmFormGroup"
            sectionTitle="Total Payment Management"
        >
            <form [formGroup]="tpmFormGroup" [style.padding]="'2rem 3rem'">
                <lsq-label
                    label="FBO Bank Account"
                    subLabel="Please note that when adding an FBO account, at least one routing number (ACH or Wire) must be entered."
                ></lsq-label>
                <ng-container *ngIf="!!(bankAccount$ | async) && !(isUpdatingBankAccount$ | async)">
                    <data-card>
                        <p id="fboExistingBankAccountName" class="label">
                            {{ (bankAccount$ | async).bankAccountName }} ({{
                                (bankAccount$ | async).bankAccountBankName
                            }})
                        </p>
                        <p id="fboExistingBankAccountNumber">
                            <b>Account Number: </b>*****{{
                                (bankAccount$ | async).accountNumberLastFour
                            }}
                        </p>
                        <p id="fboExistingBankAccountAchRoutingNumber">
                            <b>
                                ACH Routing Number:
                            </b>
                            {{
                                !!(bankAccount$ | async).achRoutingNumberLastFour
                                    ? '*****' + (bankAccount$ | async).achRoutingNumberLastFour
                                    : 'Not set'
                            }}
                        </p>
                        <p id="fboExistingBankAccountWireRoutingNumber">
                            <b>
                                Wire Routing Number:
                            </b>
                            {{
                                !!(bankAccount$ | async).wireRoutingNumberLastFour
                                    ? '*****' + (bankAccount$ | async).wireRoutingNumberLastFour
                                    : 'Not set'
                            }}
                        </p>
                        <p id="existingBankAccountRoutingNumber">
                            <lsq-button
                                appearance="transparent"
                                (buttonClick)="setIsUpdatingBankAccount(true)"
                            >
                                Edit
                            </lsq-button>
                        </p></data-card
                    >
                </ng-container>
                <ng-container *ngIf="!(bankAccount$ | async) || (isUpdatingBankAccount$ | async)">
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 order-md-1">
                            <lsq-label
                                label="Bank Account Name*"
                                subLabel="Enter the name for the bank account exactly as it appears from the bank or statement."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-3">
                            <lsq-input
                                [basicLabel]="true"
                                name="bankAccountName"
                                formControlName="bankAccountName"
                                [group]="tpmFormGroup"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12 col-lg-6 order-md-2">
                            <lsq-label
                                [style.marginBottom]="'3rem'"
                                label="Bank Name*"
                                subLabel="The name of the bank."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-4">
                            <lsq-input
                                [basicLabel]="true"
                                name="bankName"
                                formControlName="bankName"
                                [group]="tpmFormGroup"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 order-md-1">
                            <lsq-label
                                label="Account Number*"
                                subLabel="The 6 to 16-digit account number of the bank account."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-3">
                            <lsq-input
                                [basicLabel]="true"
                                name="bankAccountNumber"
                                formControlName="bankAccountNumber"
                                [group]="tpmFormGroup"
                                [maxLength]="accountNumberMaxLength"
                                [noPaste]="true"
                                [digitsOnly]="true"
                                [type]="'number'"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12 col-lg-6 order-md-2">
                            <lsq-label
                                label="Verify Account Number*"
                                subLabel="Please re-enter the account number."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-4">
                            <lsq-input
                                [basicLabel]="true"
                                name="verifyBankAccountNumber"
                                formControlName="verifyBankAccountNumber"
                                [group]="tpmFormGroup"
                                [maxLength]="accountNumberMaxLength"
                                [noPaste]="true"
                                [type]="'number'"
                                [digitsOnly]="true"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <div class="row" [style.marginBottom]="'1rem'">
                        <div class="col-12 col-lg-6 order-md-1">
                            <lsq-label
                                label="ACH Routing Number"
                                subLabel="The 9-digit ACH routing number of the bank."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-3">
                            <lsq-input
                                [basicLabel]="true"
                                name="achRoutingNumber"
                                formControlName="achRoutingNumber"
                                [group]="tpmFormGroup"
                                [maxLength]="routingNumberMaxLength"
                                [type]="'number'"
                                [digitsOnly]="true"
                            >
                            </lsq-input>
                        </div>
                        <div class="col-12 col-lg-6 order-md-2">
                            <lsq-label
                                label="Wire Routing Number"
                                subLabel="The 9-digit wire routing number of the bank."
                            ></lsq-label>
                        </div>
                        <div class="col-12 col-lg-6 order-md-4">
                            <lsq-input
                                [basicLabel]="true"
                                name="wireRoutingNumber"
                                formControlName="wireRoutingNumber"
                                [group]="tpmFormGroup"
                                [maxLength]="routingNumberMaxLength"
                                [type]="'number'"
                                [digitsOnly]="true"
                            >
                            </lsq-input>
                        </div>
                    </div>
                    <lsq-button
                        *ngIf="!!(bankAccount$ | async)"
                        appearance="secondary"
                        (buttonClick)="setIsUpdatingBankAccount(false)"
                    >
                        Cancel
                    </lsq-button>
                </ng-container>

                <div class="divider"></div>
                <div class="row">
                    <div class="col-12 col-lg-6 order-md-1">
                        <lsq-label
                            label="ACH Company ID"
                            subLabel="The 10-digit ID of the buyer as it appears from the bank or statement."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-3" [style.marginBottom]="'1rem'">
                        <lsq-input
                            [basicLabel]="true"
                            name="companyId"
                            formControlName="companyId"
                            [group]="tpmFormGroup"
                        >
                        </lsq-input>
                    </div>
                    <div class="col-12 col-lg-6 order-md-2">
                        <lsq-label
                            label="ACH Sender ID"
                            subLabel="This is the sender name that will be seen on the bank statement when LSQ sends money to the supplier."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-4" [style.marginBottom]="'1rem'">
                        <lsq-input
                            [basicLabel]="true"
                            name="senderId"
                            formControlName="senderId"
                            [group]="tpmFormGroup"
                        >
                        </lsq-input>
                    </div>
                </div>
                <div class="row">
                    <div class="col-12 col-lg-6 order-md-1">
                        <lsq-label
                            label="FBO Account Type*"
                            subLabel="The bank to use for FBO configuration."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-3" [style.marginBottom]="'1rem'">
                        <lsq-select
                            [basicLabel]="true"
                            name="fboAccountType"
                            formControlName="fboAccountType"
                            [options]="fboAccountTypeOptions"
                            [group]="tpmFormGroup"
                        >
                        </lsq-select>
                    </div>
                    <div class="col-12 col-lg-6 order-md-2">
                        <lsq-label
                            label="Payment Instruction Output Folder ID*"
                            subLabel="The ID of the Google Drive folder for the buyer."
                        ></lsq-label>
                    </div>
                    <div class="col-12 col-lg-6 order-md-4" [style.marginBottom]="'1rem'">
                        <lsq-input
                            [basicLabel]="true"
                            name="outputFolderId"
                            formControlName="outputFolderId"
                            [group]="tpmFormGroup"
                        >
                        </lsq-input>
                    </div>
                </div>
            </form>
        </buyer-configuration-section>
    `
})
export class AdminBuyerConfigurationTpmComponent {
    @Input() public tpmFormGroup: FormGroup;
    @Input() public hasClickedSave: boolean;

    public readonly accountNumberMaxLength = ACCOUNT_NUMBER_MAX_LENGTH;
    public readonly routingNumberMaxLength = ROUTING_NUMBER_LENGTH;

    public fboAccountTypeOptions: ISelectOption[] = [
        {
            label: 'Bank of America',
            value: FboAccountType.Bank_Of_America
        },
        {
            label: 'Citizens',
            value: FboAccountType.Citizens
        },
        {
            label: 'J.P. Morgan',
            value: FboAccountType.JP_Morgan
        },
        {
            label: 'Regions Bank',
            value: FboAccountType.Regions_Bank
        },
        {
            label: 'U.S. Bank',
            value: FboAccountType.US_Bank
        }
    ].sort((a, b) => (a.label > b.label ? 1 : -1));

    public bankAccount$ = this._adminManageBuyerConfigurationService.tpmBankAccount$;
    public isUpdatingBankAccount$ = this._adminManageBuyerConfigurationService
        .isUpdatingTpmBankAccount$;

    constructor(
        private _adminManageBuyerConfigurationService: AdminManageBuyerConfigurationService
    ) {}

    public setIsUpdatingBankAccount(isUpdating: boolean): void {
        this._adminManageBuyerConfigurationService.setIsUpdatingTpmBankAccount(isUpdating);
    }
}
